import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { UploadSimple, FilePdf, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATEGORY_ICON, intervalText } from "./complianceUtils";

const today = () => new Date().toISOString().slice(0, 10);
const addMonths = (iso, m) => { if (!iso) return ""; const d = new Date(iso + "T00:00:00"); d.setMonth(d.getMonth() + Number(m || 0)); return d.toISOString().slice(0, 10); };

export function AddRequirementsDialog({ open, onOpenChange, types, commonSet = [], onSaved, propertyId }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPicked([]); }, [open]);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const cats = [...new Set(types.map((t) => t.category))];
  const submit = async () => {
    if (!picked.length) return toast.error("Pick at least one requirement");
    setBusy(true);
    try {
      const { data } = await API.post(`/compliance/properties/${propertyId}/requirements`, { type_keys: picked });
      toast.success(`${data.created.length} requirement${data.created.length !== 1 ? "s" : ""} added`);
      onSaved?.(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't add"); }
    finally { setBusy(false); }
  };
  const common = types.filter((t) => commonSet.includes(t.key)).map((t) => t.key);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-requirements-dialog">
        <DialogHeader><DialogTitle>What must this building hold?</DialogTitle><DialogDescription>Tick every statutory certificate or inspection that applies. You'll record dates and upload certificates afterwards.</DialogDescription></DialogHeader>
        {common.length > 0 && <Button variant="outline" size="sm" onClick={() => setPicked((p) => [...new Set([...p, ...common])])} data-testid="add-common-set">Tick the common set ({common.length})</Button>}
        <div className="space-y-3">
          {cats.map((c) => {
            const I = CATEGORY_ICON[c] || CATEGORY_ICON.Other;
            return (
              <div key={c}>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><I size={12} /> {c}</p>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {types.filter((t) => t.category === c).map((t) => (
                    <label key={t.key} className={`flex items-start gap-2 rounded-md border p-2 text-sm cursor-pointer transition-colors ${picked.includes(t.key) ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid={`req-option-${t.key}`}>
                      <input type="checkbox" checked={picked.includes(t.key)} onChange={() => toggle(t.key)} className="mt-0.5" />
                      <span><span className="font-medium">{t.name}</span><br /><span className="text-[11px] text-muted-foreground">Every {intervalText(t.interval_months)} · {t.who}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          {types.length === 0 && <p className="text-sm text-muted-foreground">Every library type is already on this building. Add a custom type from the Types tab if you need another.</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy || !picked.length} data-testid="add-requirements-submit">{busy ? "Adding…" : `Add ${picked.length || ""}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CertificateDialog({ open, onOpenChange, record, onSaved }) {
  const [f, setF] = useState({ last_inspection_date: today(), expiry_date: "", provider: "", reference: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && record) { setF({ last_inspection_date: today(), expiry_date: addMonths(today(), record.interval_months), provider: record.provider || "", reference: "", notes: "" }); setFile(null); }
  }, [open, record]);
  if (!record) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("file", file);
      await API.post(`/compliance/records/${record.id}/certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${record.type_short} updated — next due ${f.expiry_date || "per interval"}`);
      onSaved?.(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="certificate-dialog">
        <DialogHeader><DialogTitle>Record inspection — {record.type_name}</DialogTitle><DialogDescription>{record.property_name}. The previous certificate is kept in the history for audit.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Inspection date</Label><Input type="date" value={f.last_inspection_date} onChange={(e) => setF({ ...f, last_inspection_date: e.target.value, expiry_date: addMonths(e.target.value, record.interval_months) })} required className="mt-1" data-testid="cert-inspection-date" /></div>
            <div><Label>Expiry / next due</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className="mt-1" data-testid="cert-expiry-date" /><p className="text-[10px] text-muted-foreground mt-0.5">Defaults to +{intervalText(record.interval_months)}</p></div>
            <div><Label>Provider / engineer</Label><Input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} className="mt-1" placeholder="Company or engineer" data-testid="cert-provider" /></div>
            <div><Label>Certificate reference</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1" placeholder="Cert / report no." data-testid="cert-reference" /></div>
          </div>
          <div>
            <Label>Certificate file (PDF or image)</Label>
            <label className={`mt-1 flex items-center gap-3 rounded-lg border-2 border-dashed p-3 cursor-pointer transition-colors ${file ? "border-primary/60 bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="cert-dropzone">
              <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cert-file-input" />
              {file ? (file.type === "application/pdf" ? <FilePdf size={22} className="text-red-500" /> : <ImageIcon size={22} />) : <UploadSimple size={22} className="text-muted-foreground" />}
              <span className="text-sm">{file ? <>{file.name} <span className="text-muted-foreground">({Math.round(file.size / 1024)} KB)</span></> : <span className="text-muted-foreground">Click to attach the certificate — it goes into the property's File Hub folder and the audit pack</span>}</span>
            </label>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" placeholder="Remedials, observations, C1/C2 codes…" data-testid="cert-notes" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="cert-submit">{busy ? "Saving…" : "Save inspection"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditRecordDialog({ open, onOpenChange, record, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && record) setF({ lead_days: record.lead_days, interval_months: record.interval_months, auto_raise_job: record.auto_raise_job !== false, notes: record.notes || "", expiry_override: record.expiry_override || "" }); }, [open, record]);
  if (!record) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await API.put(`/compliance/records/${record.id}`, f); toast.success("Requirement updated"); onSaved?.(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-record-dialog">
        <DialogHeader><DialogTitle>{record.type_name}</DialogTitle><DialogDescription>Settings for this requirement at {record.property_name} only.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Interval (months)</Label><Input type="number" min="1" value={f.interval_months ?? ""} onChange={(e) => setF({ ...f, interval_months: e.target.value })} className="mt-1" data-testid="edit-interval" /></div>
            <div><Label>Lead time (days)</Label><Input type="number" min="0" value={f.lead_days ?? ""} onChange={(e) => setF({ ...f, lead_days: e.target.value })} className="mt-1" data-testid="edit-lead" /></div>
          </div>
          <div><Label>Expiry override</Label><Input type="date" value={f.expiry_override || ""} onChange={(e) => setF({ ...f, expiry_override: e.target.value })} className="mt-1" data-testid="edit-expiry-override" /><p className="text-[10px] text-muted-foreground mt-0.5">Leave blank to derive from the inspection date + interval.</p></div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div><p className="text-sm font-medium">Auto-raise a renewal job</p><p className="text-[11px] text-muted-foreground">Creates a job {f.lead_days ?? record.lead_days} days before expiry, once per certificate.</p></div>
            <Switch checked={!!f.auto_raise_job} onCheckedChange={(v) => setF({ ...f, auto_raise_job: v })} data-testid="edit-auto-job" />
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" data-testid="edit-notes" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="edit-record-submit">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
